import type React from 'react';
import {type CSSProperties, useMemo} from 'react';
import type {Theme} from '../../../styles/themes';
import styles from './ThemeProvider.module.scss';

interface ThemeProviderProps extends React.HTMLAttributes<HTMLDivElement> {
  options?: Theme | {[key: string]: string};
  prefix?: string;
}

const toKebabCase = (str: string): string => {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

const ThemeProvider = ({
  options = {},
  style = {},
  className = '',
  children,
  prefix,
  ...props
}: ThemeProviderProps): React.JSX.Element => {
  const cssVariables = useMemo(() => {
    return Object.entries(options).reduce((acc, [key, value]) => {
      let varName = '';
      if (prefix) {
        // Logic: --prefix-key-name (e.g., --pixel-party-v2-primary)
        varName = `--${prefix}-${toKebabCase(key)}`;
      } else {
        // Logic: --key-name (e.g., --primary)
        varName = `--${toKebabCase(key)}`;
      }
      return {...acc, [varName]: value};
    }, {} as CSSProperties);
  }, [options, prefix]);

  return (
    <div
      className={`theme-container ${styles.root} ${className}`.trim()}
      style={{...cssVariables, ...style}}
      {...props}
    >
      {children}
    </div>
  );
};

export {ThemeProvider};
export type {ThemeProviderProps};
